import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';

interface TicketTier {
  id: string;
  name: string;
  price: number | string;
  description: string;
  active: boolean;
}

interface TierTicketsSoldRecord {
  tierId: string;
  tierName: string;
  tierPrice: number;
  count: number;
  compCount?: number;
  setAt: string;
  setBy: string;
  setByName: string;
}

export interface TieredRevenueInputProps {
  onTotalsChange: (totalTickets: number, totalRevenue: number) => void;
}

interface TierEntry {
  tierId: string;
  tierName: string;
  tierPrice: number;
  currentTotal: number;
  compCount: number;
  newSales: string;
  error: string;
}

const styles: Record<string, React.CSSProperties> = {
  section: {
    marginBottom: '2rem',
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f',
    fontSize: '1.2rem',
    marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c',
    paddingBottom: '0.25rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '1rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#555',
    textTransform: 'uppercase' as const,
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '0.9rem',
    verticalAlign: 'middle' as const,
  },
  input: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '0.9rem',
    width: '80px',
  },
  inputError: {
    padding: '0.5rem 0.75rem',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.9rem',
    width: '80px',
  },
  btn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginRight: '0.5rem',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  fieldError: {
    color: '#d32f2f',
    fontSize: '0.75rem',
    marginTop: '0.25rem',
  },
  empty: {
    color: '#666',
    fontStyle: 'italic' as const,
    padding: '1rem 0',
  },
  totalsRow: {
    fontWeight: 700,
    backgroundColor: '#f9f9f9',
  },
  buttonRow: {
    marginTop: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  checkboxRow: {
    marginBottom: '1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  lastUpdated: {
    marginTop: '0.75rem',
    fontSize: '0.85rem',
    color: '#666',
    fontStyle: 'italic' as const,
  },
};

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function validateNewSales(value: string): { valid: boolean; count: number } {
  if (value === '' || value.trim() === '') {
    return { valid: true, count: 0 };
  }
  const num = Number(value);
  if (!Number.isInteger(num) || num < 0) {
    return { valid: false, count: 0 };
  }
  return { valid: true, count: num };
}

export function computeProjectedTotal(currentTotal: number, newSales: string): number {
  const { valid, count } = validateNewSales(newSales);
  return valid ? currentTotal + count : currentTotal;
}

export function computeTotalRevenue(entries: TierEntry[]): number {
  return entries.reduce((sum, entry) => {
    const projected = computeProjectedTotal(entry.currentTotal, entry.newSales);
    return sum + (projected - (entry.compCount || 0)) * entry.tierPrice;
  }, 0);
}

export function findMostRecentUpdate(records: TierTicketsSoldRecord[]): {
  name: string | null;
  timestamp: string | null;
} {
  if (records.length === 0) return { name: null, timestamp: null };
  const mostRecent = records.reduce((latest, record) =>
    new Date(record.setAt) > new Date(latest.setAt) ? record : latest,
  );
  return { name: mostRecent.setByName, timestamp: mostRecent.setAt };
}

export function formatLastUpdated(name: string, timestamp: string): string {
  const date = new Date(timestamp);
  const formatted = date.toLocaleString();
  return `Last updated by ${name} on ${formatted}`;
}

export function TieredRevenueInput({ onTotalsChange }: TieredRevenueInputProps) {
  const [entries, setEntries] = useState<TierEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [generateTickets, setGenerateTickets] = useState(false);
  const [lastUpdatedBy, setLastUpdatedBy] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

  const notifyParent = useCallback(
    (currentEntries: TierEntry[]) => {
      let totalTickets = 0;
      let totalRevenue = 0;
      for (const entry of currentEntries) {
        const projected = computeProjectedTotal(entry.currentTotal, entry.newSales);
        totalTickets += projected;
        totalRevenue += (projected - (entry.compCount || 0)) * entry.tierPrice;
      }
      onTotalsChange(totalTickets, totalRevenue);
    },
    [onTotalsChange],
  );

  const refreshFromRecords = useCallback(
    (savedRecords: TierTicketsSoldRecord[], tiers: TicketTier[]) => {
      const savedMap = new Map<string, number>();
      for (const record of savedRecords) {
        savedMap.set(record.tierId, record.count);
      }

      const tierEntries: TierEntry[] = tiers.map((tier) => ({
        tierId: tier.id,
        tierName: tier.name,
        tierPrice: Number(tier.price),
        currentTotal: savedMap.get(tier.id) ?? 0,
        compCount: savedRecords.find((r) => r.tierId === tier.id)?.compCount ?? 0,
        newSales: '0',
        error: '',
      }));

      setEntries(tierEntries);
      notifyParent(tierEntries);

      // Update last-updated display
      const { name, timestamp } = findMostRecentUpdate(savedRecords);
      setLastUpdatedBy(name);
      setLastUpdatedAt(timestamp);
    },
    [notifyParent],
  );

  useEffect(() => {
    async function fetchData() {
      try {
        const [tiers, savedRecords] = await Promise.all([
          apiClient<TicketTier[]>('/legacy-ball/tiers'),
          apiClient<TierTicketsSoldRecord[]>('/legacy-ball/tier-tickets-sold').catch(
            () => [] as TierTicketsSoldRecord[],
          ),
        ]);

        refreshFromRecords(savedRecords, tiers);
        setError('');
      } catch (err: unknown) {
        const apiErr = err as { message?: string };
        setError(apiErr.message || 'Failed to load tier data.');
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [refreshFromRecords]);

  function handleNewSalesChange(index: number, value: string) {
    setEntries((prev) => {
      const updated = [...prev];
      const { valid } = validateNewSales(value);
      updated[index] = {
        ...updated[index],
        newSales: value,
        error: valid ? '' : 'Must be a non-negative integer',
      };
      notifyParent(updated);
      return updated;
    });
  }

  function hasValidationErrors(): boolean {
    return entries.some((entry) => {
      const { valid } = validateNewSales(entry.newSales);
      return !valid;
    });
  }

  async function handleSave() {
    if (hasValidationErrors()) return;

    setSaving(true);
    setError('');

    try {
      // Step 1: Fetch current counts from server (fresh)
      const currentRecords = await apiClient<TierTicketsSoldRecord[]>(
        '/legacy-ball/tier-tickets-sold',
      );

      // Step 2: Compute new totals (fetched + new sales)
      const currentMap = new Map(currentRecords.map((r) => [r.tierId, r.count]));
      const payload = entries.map((entry) => {
        const { count: newSalesCount } = validateNewSales(entry.newSales);
        const fetchedCurrent = currentMap.get(entry.tierId) ?? 0;
        return { tierId: entry.tierId, count: fetchedCurrent + newSalesCount };
      });

      // Step 3: PUT computed totals
      await apiClient('/legacy-ball/tier-tickets-sold', {
        method: 'PUT',
        body: { entries: payload },
      });

      // Step 4: If checkbox checked, bulk-generate for tiers with new sales > 0
      if (generateTickets) {
        const generateErrors: string[] = [];
        for (const entry of entries) {
          const { count: qty } = validateNewSales(entry.newSales);
          if (qty > 0) {
            try {
              await apiClient('/legacy-ball/ticket-inventory/bulk-generate', {
                method: 'POST',
                body: { tierId: entry.tierId, quantity: qty },
              });
            } catch {
              generateErrors.push(entry.tierName);
            }
          }
        }
        if (generateErrors.length > 0) {
          setError(`Failed to generate tickets for: ${generateErrors.join(', ')}`);
        }
      }

      // Step 5: Refresh - fetch updated records and reset new sales
      const updatedRecords = await apiClient<TierTicketsSoldRecord[]>(
        '/legacy-ball/tier-tickets-sold',
      );
      const tiers = await apiClient<TicketTier[]>('/legacy-ball/tiers');
      refreshFromRecords(updatedRecords, tiers);
      setGenerateTickets(false);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to save tier tickets sold.');
      // Preserve entered newSales values on failure
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Tiered Revenue Input</h2>
        <p style={{ color: '#666' }}>Loading tier data...</p>
      </div>
    );
  }

  if (entries.length === 0 && !error) {
    return (
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Tiered Revenue Input</h2>
        <p style={styles.empty}>No tiers configured</p>
      </div>
    );
  }

  // Calculate totals for display
  let totalCurrentTotal = 0;
  let totalNewSales = 0;
  let totalProjected = 0;
  let totalRevenue = 0;

  const rowData = entries.map((entry) => {
    const { valid, count: newSalesCount } = validateNewSales(entry.newSales);
    const projected = computeProjectedTotal(entry.currentTotal, entry.newSales);
    const revenue = projected * entry.tierPrice;

    totalCurrentTotal += entry.currentTotal;
    if (valid) {
      totalNewSales += newSalesCount;
    }
    totalProjected += projected;
    totalRevenue += revenue;

    return { ...entry, newSalesCount: valid ? newSalesCount : 0, projected, revenue };
  });

  return (
    <div style={styles.section}>
      <h2 style={styles.sectionTitle}>Tiered Revenue Input</h2>
      {error && <div style={styles.error}>{error}</div>}

      {/* Generate Tickets Checkbox */}
      <div style={styles.checkboxRow}>
        <input
          type="checkbox"
          id="generate-tickets-checkbox"
          checked={generateTickets}
          onChange={(e) => setGenerateTickets(e.target.checked)}
        />
        <label htmlFor="generate-tickets-checkbox">
          Generate unassigned tickets for new sales
        </label>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Tier Name</th>
            <th style={styles.th}>Price</th>
            <th style={styles.th}>Current Total</th>
            <th style={styles.th}>New Sales</th>
            <th style={styles.th}>Projected Total</th>
            <th style={styles.th}>Revenue</th>
          </tr>
        </thead>
        <tbody>
          {rowData.map((row, index) => (
            <tr key={row.tierId}>
              <td style={styles.td}>
                <strong>{row.tierName}</strong>
              </td>
              <td style={styles.td}>{formatCurrency(row.tierPrice)}</td>
              <td style={styles.td}>{row.currentTotal}</td>
              <td style={styles.td}>
                <input
                  type="number"
                  min="0"
                  step="1"
                  style={row.error ? styles.inputError : styles.input}
                  value={row.newSales}
                  onChange={(e) => handleNewSalesChange(index, e.target.value)}
                  aria-label={`New sales for ${row.tierName}`}
                />
                {row.error && <div style={styles.fieldError}>{row.error}</div>}
              </td>
              <td style={styles.td}>{row.projected}</td>
              <td style={styles.td}>{formatCurrency(row.revenue)}</td>
            </tr>
          ))}
          <tr style={styles.totalsRow}>
            <td style={styles.td}>Total</td>
            <td style={styles.td}></td>
            <td style={styles.td}>{totalCurrentTotal}</td>
            <td style={styles.td}>{totalNewSales}</td>
            <td style={styles.td}>{totalProjected}</td>
            <td style={styles.td}>{formatCurrency(totalRevenue)}</td>
          </tr>
        </tbody>
      </table>

      <div style={styles.buttonRow}>
        <button
          type="button"
          style={styles.btn}
          onClick={handleSave}
          disabled={saving || hasValidationErrors()}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* Last Updated By Display */}
      {lastUpdatedBy && lastUpdatedAt && (
        <div style={styles.lastUpdated}>
          {formatLastUpdated(lastUpdatedBy, lastUpdatedAt)}
        </div>
      )}
    </div>
  );
}
